import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { setCheckOutConfirmIsOpen } from 'redux/checkOutConfirmInfoSlice'
import useWindowDimensions from 'customHook/useWindowDimensions'
import Spinner from 'components/Spinner';
import EighthFloor from '../FloorPlan/floors/EighthFloor'
import NinthFloor from '../FloorPlan/floors/NinthFloor'
import TenthFloor from '../FloorPlan/floors/TenthFloor'
import Legend from '../Legend'
import ModalCheckOutConfirm from "screens/ModalCheckOutConfirm";
import { useQuery } from '@apollo/client';
import { GET_ALL_ROOMS, SUBSCRIBE_TO_ROOMS_UPDATED } from 'graphql/service';
import ActiveMapLegend from 'assets/icons/activeMapLegend.svg'
import MapLegend from 'assets/icons/mapLegend.svg'
import { Container, Header, ViewLegend, MapRow } from './style';
import colors from 'constants/colors'
import { debouncedErrorMessage } from 'utils/messageUtil'

const ManageRooms = () => {

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { width } =useWindowDimensions()
  const scale = width/1440 * 1.35
  const [isLegendVisible, setIsLegendVisible] = useState(false)

  const checkOutConfirmInfo = useSelector(
    (state) => state.checkOutConfirmInfoReducer
  );

  const {
    loading,
    data: roomsData,
    error,
    subscribeToMore,
  } = useQuery(GET_ALL_ROOMS, {
    fetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true
  });

  const { eighthFloorRooms, ninthFloorRooms, tenthFloorRooms } = useMemo(() => {
    const result = {
      eighthFloorRooms: {},
      ninthFloorRooms: {},
      tenthFloorRooms: {},
    };

    if (!roomsData?.validRooms) return result;

    roomsData.validRooms.forEach(room => {
      switch (room.floor) {
        case '8':
          result.eighthFloorRooms[room.roomNumber] = room;
          break;
        case '9':
          result.ninthFloorRooms[room.roomNumber] = room;
          break;
        case '10':
          result.tenthFloorRooms[room.roomNumber] = room;
          break;
        default:
          break;
      }
    });

    return result;
  }, [roomsData]);

  useEffect(() => {
    if (!error) return;

    if (!error.networkError) {
      let content = error.message;

      if (error.message.includes('Validation')) {
        content = JSON.stringify(
          error.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [error]);

  useEffect(() => {

    if(!subscribeToMore) return;
    if (roomsData) {
      const unsubscribe = subscribeToMore({
        document: SUBSCRIBE_TO_ROOMS_UPDATED,
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData.data?.roomMapUpdated) return;
          const updatedRooms = subscriptionData.data?.roomMapUpdated;
          if (updatedRooms.length === 0) return prev;
          const mergedRooms = prev.validRooms.map((room) => {
            const updated = updatedRooms.find(
              (element) => element.roomId === room.roomId
            );
            return updated ? updated : room;
          });

          return {
            ...prev,
            validRooms: [...mergedRooms],
          };
        },
      });

      return () => {
        unsubscribe();
      };
    }
  }, [roomsData, subscribeToMore]);
    
  if (loading) return <Spinner />;

  return (
    <Container>
      <Header>
        <ViewLegend
          onClick={() => setIsLegendVisible(!isLegendVisible)}
          active={isLegendVisible}
        >
          {isLegendVisible ?
            <img src={ActiveMapLegend} alt={'activeMapLegend'} /> :
            <img src={MapLegend} alt={'mapLegend'} />}
          <div>{isLegendVisible ? 'HIDE LEGEND' : 'VIEW LEGEND'}</div>
        </ViewLegend>
      </Header>
      { isLegendVisible? <Legend desktop/> : null}
      <MapRow scale={scale}>
        <div className='floor'>
          <div
            className='floorNumber'
            onClick={()=>navigate('/floor/8')}
          >
            <span className='type'>FLOOR</span>
            <span className='number'>8</span>
          </div>
          <EighthFloor scale={scale} backgroundColor={colors.black1} rooms={eighthFloorRooms}/>
        </div>
        <div className='floor'>
          <div
            className='floorNumber'
            onClick={()=>navigate('/floor/9')}
          >
            <span className='type'>FLOOR</span>
            <span className='number'>9</span>
          </div>
          <NinthFloor scale={scale} backgroundColor={colors.black1} rooms={ninthFloorRooms}/>
        </div>
        <div className='floor'>
          <div
            className='floorNumber'
            onClick={()=>navigate('/floor/10')}
          >
            <span className='type'>FLOOR</span>
            <span className='number'>10</span>
          </div>
          <TenthFloor scale={scale} backgroundColor={colors.black1} rooms={tenthFloorRooms}/>
        </div>
      </MapRow>
      <ModalCheckOutConfirm
        isOpen={checkOutConfirmInfo.checkOutConfirmIsOpen}
        setIsOpen={(e) => dispatch(setCheckOutConfirmIsOpen(e))}
        roomString={"room " + checkOutConfirmInfo.roomNumber}
        checkOutBookings={checkOutConfirmInfo.checkOutBookings}
      />
    </Container>
  );
};

export default ManageRooms;
